import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Directive,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, catchError, from, map, mergeMap, switchMap } from 'rxjs';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { KonfigPillComponent } from '../../components/konfig-pill/konfig-pill.component';
import { DefectRefChipsComponent } from '../../components/defect-ref-chips/defect-ref-chips.component';
import { Button } from '@spirit-testing/component-library';
import { newest } from '../ci-mapping/ci-mapping-redirect.guard';
import { TestprotokollDialogComponent } from './testprotokoll-dialog/testprotokoll-dialog.component';
import { DefectErfassenDialogComponent } from '../../components/defect-erfassen-dialog/defect-erfassen-dialog.component';
import { canCaptureDefect, withDefect } from '../../components/defect-erfassen-dialog/defect-capture';
import { Defect } from '../../models/defect.model';
import { ProjectService } from '../../services/project.service';
import { TestRunCommentService } from '../../services/test-run-comment.service';
import { TestRunService } from '../../services/test-run.service';
import { TestlaufRunnerService } from '../../services/testlauf-runner.service';
import { TestRunComment } from '../../models/test-run-comment.model';
import { ProjectRoleService } from '../../services/project-role.service';
import { StepBugLinkService } from '../../services/step-bug-link.service';
import {
  ResultStatus,
  RunnerState,
  TestCaseResult,
  CreatedBug,
  TestRun,
  TestRunExecution,
  TestRunSource,
  TestRunStatus,
  TestStepResult,
} from '../../models/test-run.model';
import { ProviderType } from '../../models/project-integration.model';
import { sourceToSystemKey, statusKey, systemMeta } from '../test-results/test-results.types';
import { providerColor, providerLabel } from '../../shared/provider-meta';
import { NO_BUG_TARGET_CHOICE, resolveBugTargets } from '../../components/bug-target-picker/bug-target-choice';
import {
  BugticketDialogComponent,
  BugticketDialogRequest,
} from '../../components/bugticket-dialog/bugticket-dialog.component';
import { deriveRunOutcome } from '../../shared/test-run-outcome';
import { formatDurationSeconds } from '../../shared/duration.util';
import { findRouteParam } from '../../shared/route-params';
import { fillStepResult, resolveIterationValues } from '../testlauf-runner/iteration-values';
import { callLine, callReason } from '../testlauf-runner/call-reason';
import {
  HeaderPath,
  ResultNode,
  ResultRow,
  headerPath,
  isHidden,
  passLabel,
  resultRows,
} from '../testlauf-runner/result-rows';

/** Sources whose runs are produced by a tool rather than a person. */
const AUTOMATED_SOURCES = new Set(['ROBOT', 'JUNIT', 'PIPELINE', 'TCDL', 'IMPORT']);
const FINISHED_RUN_STATUSES = new Set<TestRunStatus>(['COMPLETED', 'ABORTED']);

/** Accepts a Jira key (PROJ-123) or a numeric Redmine id — provider-neutral (NEX-781). */
const ISSUE_KEY_PATTERN = /^([A-Z][A-Z0-9]+-\d+|\d+)$/;

/** Material icon for the run outcome badge. */
const OUTCOME_ICON: Record<ResultStatus, string> = {
  PASSED: 'check',
  FAILED: 'close',
  BLOCKED: 'shield',
  RUNNING: 'sync',
  SKIPPED: 'remove',
  NOT_RUN: 'remove',
};

/** Im Bundle tragen nur Eintraege mit diesen Ergebnissen eine Marke. */
const COMMENT_STATUSES = new Set<ResultStatus>(['PASSED', 'FAILED', 'BLOCKED', 'SKIPPED']);

type LoadState = 'loading' | 'ready' | 'error';

interface CommentStep {
  number: string;
  caseKey: string;
  caseTitle: string;
  status: ResultStatus | null;
}

interface CommentEntry {
  comment: TestRunComment;
  step: CommentStep | null;
  /** Gesetzt genau bei einer Uebergabe. */
  handoverKey: string | null;
}

interface EvidenceItem {
  id: string;
  url: string;
  filename: string;
}

/** One rendered step row: a leaf result plus its display metadata. */
interface DisplayStep {
  result: TestStepResult;
  number: string;
  group: string | null;
  /** Kennung des Modulknotens dieser Gruppe — siehe {@link Leaf}. */
  groupId: string | null;
  /** Status des Modulknotens dieser Gruppe — siehe {@link Leaf}. */
  groupStatus: ResultStatus | null;
  /** Durchgang des Modulknotens dieser Gruppe — siehe {@link Leaf}. */
  groupIteration: number | null;
  indented: boolean;
  /** True on the first row of a new group so the header is drawn once. */
  showGroupHeader: boolean;
  /** Ebene im Baum eines Laufs mit Aufruf-Zustand (NEX-2282); im Bestand {@code null}. */
  level: number | null;
}

/** Schritte eines Testfalls unter seinem Kopf, sobald ein Lauf mehrere Testfaelle hat (NEX-1498). */
interface CaseGroup {
  id: string;
  testCaseId: string | null;
  key: string;
  title: string;
  status: ResultStatus;
  configuration: string | null;
  steps: DisplayStep[];
  /** Nur fuer Faelle mit Aufruf-Zustand: alle Knoten mit Koepfen, Runden und Aufruf-Zeilen (NEX-2282). */
  rows: ResultRow<DisplayStep>[] | null;
}

/** Typisiert den Kontext von `#stepRows`, damit `step` im Template nicht `any` ist. */
@Directive({ selector: 'ng-template[appStepRows]' })
export class StepRowsTemplateDirective {
  static ngTemplateContextGuard(
    _directive: StepRowsTemplateDirective,
    context: unknown,
  ): context is { $implicit: DisplayStep[] } {
    return true;
  }
}

/** Typisiert den Kontext von `#stepRow`. */
@Directive({ selector: 'ng-template[appStepRow]' })
export class StepRowTemplateDirective {
  static ngTemplateContextGuard(
    _directive: StepRowTemplateDirective,
    context: unknown,
  ): context is { $implicit: DisplayStep } {
    return true;
  }
}

/** Typisiert den Kontext von `#treeRows`. */
@Directive({ selector: 'ng-template[appTreeRows]' })
export class TreeRowsTemplateDirective {
  static ngTemplateContextGuard(
    _directive: TreeRowsTemplateDirective,
    context: unknown,
  ): context is { $implicit: ResultRow<DisplayStep>[] } {
    return true;
  }
}

@Component({
  selector: 'app-test-result-detail',
  imports: [
    TranslatePipe,
    DatePipe,
    NgTemplateOutlet,
    StepRowsTemplateDirective,
    StepRowTemplateDirective,
    TreeRowsTemplateDirective,
    RouterLink,
    MatIcon,
    MatProgressSpinner,
    ProjectNavComponent,
    AvatarComponent,
    BugticketDialogComponent,
    DefectErfassenDialogComponent,
    DefectRefChipsComponent,
    KonfigPillComponent,
    TestprotokollDialogComponent,
    Button,
  ],
  templateUrl: './test-result-detail.component.html',
  styleUrl: './test-result-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [StepBugLinkService],
})
export class TestResultDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly testRunService = inject(TestRunService);
  private readonly bugLinks = inject(StepBugLinkService);
  private readonly projectService = inject(ProjectService);
  private readonly commentService = inject(TestRunCommentService);
  private readonly runnerService = inject(TestlaufRunnerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly projectRole = inject(ProjectRoleService, { optional: true });
  protected readonly isArchived = computed(() => this.projectRole?.isArchived() ?? false);
  protected readonly isReader = computed(
    () => (this.projectRole?.isProjectReader() ?? false) && !(this.projectRole?.isTenantAdmin() ?? false),
  );

  protected readonly bugTargets = this.bugLinks.creationTargets;
  protected readonly bugProviders = computed(
    () => resolveBugTargets(this.bugTargets(), NO_BUG_TARGET_CHOICE).providers,
  );
  protected readonly noBugTarget = computed(
    () => this.bugLinks.integrationsLoadState() === 'ready' && !this.bugTargets().length,
  );
  protected readonly bugTargetsFailed = computed(() => this.bugLinks.integrationsLoadState() === 'failed');
  protected readonly providerLabel = providerLabel;
  protected readonly providerColor = providerColor;

  protected readonly bugDialog = signal<BugticketDialogRequest | null>(null);

  protected readonly protocolExecution = signal<TestRunExecution | null>(null);
  protected readonly protocolCaseResultId = signal<string | null>(null);
  protected readonly protocolVisible = signal(false);

  protected readonly statusKey = statusKey;

  protected readonly projectId: string;
  protected readonly runId: string;

  protected readonly loading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly notFound = signal(false);
  protected readonly projectName = signal('');
  protected readonly tags = signal<string[]>([]);
  private readonly runnerState = signal<RunnerState | null>(null);
  private readonly runnerStateLoad = signal<LoadState>('loading');
  private readonly commentsLoad = signal<LoadState>('loading');
  private readonly comments = signal<TestRunComment[]>([]);
  private readonly run = signal<TestRun | null>(null);

  private readonly evidenceByStepResultId = signal<Record<string, EvidenceItem[]>>({});
  /** URL currently shown in the fullscreen lightbox; null = closed. */
  protected readonly lightboxUrl = signal<string | null>(null);

  protected readonly overviewLink: string[];
  /** Zurueck in die Testfall-Tabelle, aus der Historie und Detail geoeffnet werden. */
  protected readonly overviewQueryParams = { sicht: 'testfaelle' };

  protected readonly runKey = computed(() => this.run()?.key ?? '');
  private readonly source = computed<TestRunSource>(() => this.run()?.source ?? 'MANUAL');
  protected readonly systemKey = computed(() => sourceToSystemKey(this.source()));
  protected readonly system = computed(() => systemMeta(this.systemKey()));
  protected readonly automated = computed(() => AUTOMATED_SOURCES.has(this.source()));
  protected readonly pipeline = computed(() => (this.automated() ? (this.runnerState()?.pipeline ?? null) : null));
  protected readonly environment = computed(() => this.run()?.environment?.trim() || '—');
  protected readonly executedByName = computed(() => this.run()?.executedByName?.trim() || '');
  protected readonly finished = computed(() => FINISHED_RUN_STATUSES.has(this.run()?.status ?? 'DRAFT'));
  protected readonly completedAt = computed(() => this.run()?.completedAt ?? this.run()?.startedAt ?? null);

  /** Primary test case of the run — its title/key head the page. */
  private readonly primaryCase = computed(() => this.run()?.caseResults?.[0] ?? null);
  protected readonly caseTitle = computed(() => this.primaryCase()?.caseTitleSnapshot ?? '');
  protected readonly caseKey = computed(() => this.primaryCase()?.caseKeySnapshot ?? '');

  /** Leaf steps per test case in render order (modules become group headers, not rows). */
  private readonly caseLeaves = computed(() => flattenCaseLeaves(this.run()));
  private readonly leaves = computed(() => this.caseLeaves().flatMap((entry) => entry.leaves));

  protected readonly steps = computed<DisplayStep[]>(() => withGroupHeaders(this.leaves()));
  /** Ein Testfall mit Aufruf-Zustand zeigt den ganzen Baum statt nur der innersten Koepfe (NEX-2282). */
  protected readonly treeRows = computed(() => this.caseLeaves()[0]?.rows ?? null);
  /** Zugeklappte Runden- und Rückweg-Koepfe; nur in der Ansicht, Standard offen. */
  protected readonly collapsed = signal<ReadonlySet<string>>(new Set());

  protected readonly caseGroups = computed<CaseGroup[]>(() =>
    this.caseLeaves().map(({ caseResult, leaves, rows }) => ({
      id: caseResult.id,
      testCaseId: caseResult.testCaseId,
      key: caseResult.caseKeySnapshot?.trim() ?? '',
      title: caseResult.caseTitleSnapshot ?? '',
      status: caseResult.status,
      configuration: caseResult.configuration ?? null,
      steps: withGroupHeaders(leaves),
      rows,
    })),
  );
  /** Mehr als ein Testfall: Kopf nennt den Lauf, Schritte stehen je Testfall (NEX-1498). */
  protected readonly multiCase = computed(() => this.caseGroups().length > 1);
  protected readonly caseCount = computed(() => this.caseGroups().length);
  protected readonly runName = computed(() => this.run()?.name?.trim() ?? '');

  /** Solange etwas laedt, ist weder die Liste vollstaendig noch die gezeigte Runde bekannt. */
  protected readonly commentsState = computed<LoadState>(() => {
    if (this.commentsLoad() === 'error') return 'error';
    return this.commentsLoad() === 'loading' || this.runnerStateLoad() === 'loading' ? 'loading' : 'ready';
  });
  protected readonly stepCommentsFailed = computed(() => this.runnerStateLoad() === 'error');

  protected readonly commentEntries = computed<CommentEntry[]>(() => {
    const steps = commentSteps(this.caseGroups());
    const execution = this.protocolExecution();
    const runWideKey = (this.multiCase() ? '' : this.caseKey()) || this.runKey();
    return [
      ...this.comments().filter((comment) => !execution || belongsToExecution(comment, execution)),
      ...stepCommentsOf(this.runnerState()),
    ]
      .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
      .map((comment) => {
        const step = steps.get(comment.stepResultId ?? '') ?? null;
        return { comment, step, handoverKey: comment.kind === 'HANDOVER' ? step?.caseKey || runWideKey : null };
      });
  });

  protected readonly total = computed(() => this.leaves().length);
  protected readonly passed = computed(() => this.countStatus('PASSED'));
  protected readonly failed = computed(() => this.countStatus('FAILED'));
  protected readonly blocked = computed(() => this.countStatus('BLOCKED'));
  protected readonly skipped = computed(() => this.countStatus('SKIPPED'));

  /** Frozen run outcome; derived from the steps when the backend left it undecided. */
  protected readonly outcome = computed<ResultStatus>(() => this.run()?.outcome ?? this.deriveOutcome());
  protected readonly outcomeIcon = computed(() => OUTCOME_ICON[this.outcome()]);

  protected readonly runDuration = computed(() => formatDurationSeconds(this.run()?.durationSeconds ?? null));

  protected readonly unmatchedTests = computed(() => (this.automated() ? (this.run()?.unmatchedTests ?? 0) : 0));
  protected readonly unmatchedTestsKey = computed(() =>
    this.unmatchedTests() === 1 ? 'testResults.detail.unmatchedTestsOne' : 'testResults.detail.unmatchedTestsOther',
  );
  /**
   * Ohne executionId oeffnet die CI-Zuordnung den neuesten Durchlauf — und genau den
   * zeigt diese Seite.
   */
  protected readonly ciMappingLink: string[];

  constructor() {
    this.projectId = this.findParam('projectId') ?? '';
    this.runId = this.route.snapshot.paramMap.get('runId') ?? '';
    this.overviewLink = ['/projects', this.projectId, 'test-results'];
    this.ciMappingLink = ['/projects', this.projectId, 'test-runs', this.runId, 'ci-mapping'];
    this.destroyRef.onDestroy(() => {
      for (const items of Object.values(this.evidenceByStepResultId())) {
        for (const item of items) URL.revokeObjectURL(item.url);
      }
    });
  }

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.testRunService
      .get(this.projectId, this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (run) => {
          this.run.set(run);
          this.loading.set(false);
          this.loadEvidence();
          this.loadProjectName();
          this.loadTags();
          this.loadComments();
        },
        error: (err: unknown) => {
          // Auch 403: Ob es den Lauf in einem fremden Projekt gibt, verraet die Seite nicht.
          const status = err instanceof HttpErrorResponse ? err.status : 0;
          this.notFound.set(status === 404 || status === 403);
          this.loadError.set(!this.notFound());
          this.loading.set(false);
        },
      });
    this.bugLinks.load(this.projectId);
    this.loadShownExecution();
  }

  private loadProjectName(): void {
    this.projectService
      .getById(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (project) => this.projectName.set(project.name), error: () => this.projectName.set('') });
  }

  private loadTags(): void {
    this.testRunService
      .getSettings(this.projectId, this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (settings) => this.tags.set(settings.tags), error: () => this.tags.set([]) });
  }

  private loadRunnerState(executionId: string): void {
    this.runnerService
      .getState(this.projectId, this.runId, executionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.runnerState.set(state);
          this.runnerStateLoad.set('ready');
        },
        error: () => this.runnerStateLoad.set('error'),
      });
  }

  private loadComments(): void {
    this.commentsLoad.set('loading');
    this.commentService
      .listAll(this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (comments) => {
          this.comments.set(comments);
          this.commentsLoad.set('ready');
        },
        error: () => this.commentsLoad.set('error'),
      });
  }

  protected retryComments(): void {
    if (this.commentsLoad() === 'error') this.loadComments();
    if (this.runnerStateLoad() === 'error') this.loadShownExecution();
  }

  protected historyRoute(testCaseId: string): string[] {
    return [...this.overviewLink, 'history', testCaseId];
  }

  /** Diese Seite zeigt die Ergebnisse des neuesten Durchlaufs; ohne Durchlauf gibt es kein Protokoll. */
  private loadShownExecution(): void {
    this.runnerStateLoad.set('loading');
    this.testRunService
      .listExecutions(this.projectId, this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (executions) => {
          const execution = newest(executions);
          this.protocolExecution.set(execution);
          if (execution) this.loadRunnerState(execution.id);
          else this.runnerStateLoad.set('ready');
        },
        error: () => {
          this.protocolExecution.set(null);
          this.runnerStateLoad.set('error');
        },
      });
  }

  protected openProtocol(caseResultId: string): void {
    this.protocolCaseResultId.set(caseResultId);
    this.protocolVisible.set(true);
  }

  protected toggle(id: string): void {
    this.collapsed.update((ids) => {
      const next = new Set(ids);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  protected isShown(row: ResultRow<DisplayStep>): boolean {
    return !isHidden(row, this.collapsed());
  }

  protected headerOf(result: TestStepResult): HeaderPath {
    return headerPath(result.path, result.descriptionSnapshot, this.translate);
  }

  protected passLabelOf(row: ResultRow<DisplayStep>): string {
    return passLabel(row, this.translate);
  }

  protected reasonOf(result: TestStepResult): string {
    return callReason(result.callState, result.comment, result.callConditions, this.translate);
  }

  protected lineOf(result: TestStepResult): string {
    return callLine(
      result.descriptionSnapshot,
      result.callState,
      result.comment,
      result.callConditions,
      this.translate,
    );
  }

  protected stepDuration(result: TestStepResult): string {
    return formatDurationSeconds(result.durationSeconds);
  }

  protected evidenceFor(stepResultId: string): EvidenceItem[] {
    return this.evidenceByStepResultId()[stepResultId] ?? [];
  }

  protected openLightbox(url: string): void {
    this.lightboxUrl.set(url);
  }

  protected closeLightbox(): void {
    this.lightboxUrl.set(null);
  }

  /** Linked bug key of a step, but only when it is well-formed — otherwise no jump target. */
  protected linkedBugKey(result: TestStepResult): string | null {
    const key = result.linkedBugJiraKey?.trim();
    return key && ISSUE_KEY_PATTERN.test(key) ? key : null;
  }

  protected linkedBugUrl(result: TestStepResult): string | null {
    const key = this.linkedBugKey(result);
    return key
      ? this.bugLinks.exactUrlFor(result.id, key, result.linkedBugIntegrationId, result.linkedBugProviderType)
      : null;
  }

  protected openLinkedBug(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  protected openBugDialog(result: TestStepResult, stepNumber: string, provider: ProviderType): void {
    const group = this.caseGroups().find((candidate) => candidate.id === result.testCaseResultId);
    this.bugDialog.set({
      stepResultId: result.id,
      defaultTitle: this.translate.instant('testResults.detail.bug.defaultTitle', {
        title: group?.title ?? '',
        number: stepNumber,
      }),
      sourceRef: [group?.key, this.runKey()].filter(Boolean).join(' · '),
      provider,
    });
  }

  protected onBugCreated(bug: CreatedBug): void {
    const stepResultId = this.bugDialog()?.stepResultId;
    if (!stepResultId) return;
    this.bugLinks.remember(stepResultId, bug.browseUrl);
    this.applyLinkedBugKey(stepResultId, bug.key);
  }

  protected canCaptureDefect(result: TestStepResult): boolean {
    return !this.isArchived() && !this.isReader() && canCaptureDefect(result.kind, result.status);
  }

  /** Der Schritt, fuer den der Erfassen-Dialog offen ist. */
  protected readonly defectStepId = signal<string | null>(null);

  protected captureDefect(result: TestStepResult): void {
    this.defectStepId.set(result.id);
  }

  protected onDefectCreated(defect: Defect): void {
    const stepResultId = this.defectStepId();
    this.defectStepId.set(null);
    if (stepResultId) {
      this.updateStepResult(stepResultId, (step) => ({ ...step, defects: withDefect(step.defects, defect) }));
    }
  }

  private applyLinkedBugKey(stepResultId: string, key: string): void {
    this.updateStepResult(stepResultId, (step) => ({ ...step, linkedBugJiraKey: key }));
  }

  /** Immutably replaces the matching step result of the loaded run. */
  private updateStepResult(stepResultId: string, change: (step: TestStepResult) => TestStepResult): void {
    const current = this.run();
    if (!current?.caseResults) return;
    this.run.set({
      ...current,
      caseResults: current.caseResults.map((caseResult) => ({
        ...caseResult,
        stepResults: caseResult.stepResults?.map((step) => (step.id === stepResultId ? change(step) : step)) ?? null,
      })),
    });
  }

  private countStatus(status: ResultStatus): number {
    return this.leaves().filter((leaf) => leaf.result.status === status).length;
  }

  private deriveOutcome(): ResultStatus {
    return deriveRunOutcome(this.leaves().map((leaf) => leaf.result.status));
  }

  // Nachweise read-only laden: fehlende Anhänge oder fehlgeschlagene Requests werden
  // still übersprungen (kein Bild, kein Fehler-Banner) — die Seite bleibt lesbar.
  private loadEvidence(): void {
    for (const leaf of this.leaves()) {
      const stepResultId = leaf.result.id;
      this.testRunService
        .listStepResultAttachments(this.projectId, stepResultId)
        .pipe(
          switchMap((attachments) => {
            if (attachments.length === 0) return EMPTY;
            return from(attachments).pipe(
              mergeMap((attachment) =>
                this.testRunService.downloadStepResultAttachment(this.projectId, stepResultId, attachment.id).pipe(
                  map((blob) => ({ id: attachment.id, filename: attachment.originalFilename, blob })),
                  catchError(() => EMPTY),
                ),
              ),
            );
          }),
          catchError(() => EMPTY),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(({ id, filename, blob }) => {
          const url = URL.createObjectURL(blob);
          this.evidenceByStepResultId.update((map) => ({
            ...map,
            [stepResultId]: [...(map[stepResultId] ?? []), { id, url, filename }],
          }));
        });
    }
  }

  private findParam(name: string): string | null {
    return findRouteParam(this.route, name);
  }
}

/**
 * Uebergaben entstehen ohne Runde und gehoeren zu der, die zu dem Zeitpunkt lief; dieselbe Regel
 * wendet das Testprotokoll an. Die gezeigte Runde ist die neueste, also genuegt der Start.
 */
function belongsToExecution(comment: TestRunComment, execution: TestRunExecution): boolean {
  return comment.executionId
    ? comment.executionId === execution.id
    : Date.parse(comment.createdAt) >= Date.parse(execution.startedAt);
}

/** Kommentare aus dem manuellen Runner liegen am Schritt und fehlen im Listen-API; das Testprotokoll mischt sie ebenso. */
function stepCommentsOf(state: RunnerState | null): TestRunComment[] {
  if (!state) return [];
  return state.cases
    .flatMap((runnerCase) => runnerCase.steps)
    .flatMap((step) => step.comments)
    .map((comment) => ({
      id: comment.id,
      testRunId: state.runId,
      authorUserId: comment.authorUserId ?? '',
      authorName: comment.authorName,
      text: comment.body,
      kind: 'KOMMENTAR',
      stepResultId: comment.stepResultId,
      executionId: state.executionId,
      executionNr: state.nr,
      createdAt: comment.createdAt,
      updatedAt: comment.createdAt,
      editedAt: null,
      attachments: [],
    }));
}

function commentSteps(groups: CaseGroup[]): Map<string, CommentStep> {
  const steps = new Map<string, CommentStep>();
  for (const group of groups) {
    for (const { result, number } of group.rows?.map((row) => row.item) ?? group.steps) {
      steps.set(result.id, {
        number,
        caseKey: group.key,
        caseTitle: group.title,
        status: COMMENT_STATUSES.has(result.status) ? result.status : null,
      });
    }
  }
  return steps;
}

interface Leaf {
  result: TestStepResult;
  number: string;
  group: string | null;
  /**
   * Kennung des Modulknotens, nicht sein Name: Ein Modul mit Datenblatt laeuft je Zeile
   * einmal, und alle Durchlaeufe heissen gleich. Nach dem Namen gebrochen stuenden sie
   * unter einer Ueberschrift — mit dem Status des ersten Durchlaufs ueber den Zeilen des
   * zweiten (NEX-1323).
   */
  groupId: string | null;
  /**
   * Status des Modulknotens, zu dem diese Zeile gehoert. Er wird aus den Schritten
   * darunter abgeleitet (NEX-1323) — ohne ihn stuende ueber einer Gruppe bestandener
   * Schritte dauerhaft "nicht ausgefuehrt".
   */
  groupStatus: ResultStatus | null;
  /**
   * Durchgang des Modulknotens ({@code iterationIndex}, 1-basiert wie die Zeile im
   * Datenblatt); {@code null} ohne Datenblatt. Unterscheidet die gleich benannten Koepfe
   * der Durchlaeufe (NEX-1677).
   */
  groupIteration: number | null;
  indented: boolean;
}

/**
 * Flattens the nested run into leaf step rows per test case, both in render order.
 * Step results arrive flat in {@code orderIndex} order with MODULE nodes preceding
 * their children; MODULE titles become the group label of their descendants and
 * are not themselves rendered as rows. Numbering restarts per test case.
 */
function flattenCaseLeaves(
  run: TestRun | null,
): { caseResult: TestCaseResult; leaves: Leaf[]; rows: ResultRow<DisplayStep>[] | null }[] {
  if (!run?.caseResults) return [];
  return [...run.caseResults]
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((caseResult) => {
      const sorted = [...(caseResult.stepResults ?? [])].sort((a, b) => a.orderIndex - b.orderIndex);
      const steps = resolveIterationValues(sorted, fillStepResult);
      const byParent = new Map<string | null, TestStepResult[]>();
      for (const step of steps) {
        const key = step.parentStepResultId ?? null;
        (byParent.get(key) ?? byParent.set(key, []).get(key)!).push(step);
      }
      const leaves: Leaf[] = [];
      collectLeaves(
        byParent,
        null,
        '',
        { group: null, groupId: null, groupStatus: null, groupIteration: null },
        leaves,
      );
      return { caseResult, leaves, rows: treeRowsOf(byParent, steps) };
    });
}

function nodeOf(display: DisplayStep): ResultNode {
  const { result } = display;
  return {
    id: result.id,
    parentId: result.parentStepResultId,
    kind: result.kind,
    callState: result.callState,
    passIndex: result.passIndex,
    loopPhase: result.loopPhase,
    iterationIndex: result.iterationIndex,
  };
}

/** Alle Knoten in Baum-Reihenfolge mit derselben Nummer wie {@link collectLeaves}; im Bestand {@code null}. */
function treeRowsOf(
  byParent: Map<string | null, TestStepResult[]>,
  steps: readonly TestStepResult[],
): ResultRow<DisplayStep>[] | null {
  if (!steps.some((step) => step.callState != null)) return null;
  const nodes: DisplayStep[] = [];
  const visit = (parentId: string | null, prefix: string): void => {
    (byParent.get(parentId) ?? []).forEach((result, index) => {
      const number = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
      nodes.push({
        result,
        number,
        group: null,
        groupId: null,
        groupStatus: null,
        groupIteration: null,
        indented: false,
        showGroupHeader: false,
        level: null,
      });
      visit(result.id, number);
    });
  };
  visit(null, '');
  return resultRows(nodes, nodeOf).map((row) => ({ ...row, item: { ...row.item, level: row.level } }));
}

/** Marks the first row of each module group so its header is drawn once. */
function withGroupHeaders(leaves: Leaf[]): DisplayStep[] {
  let previousGroupId: string | null = null;
  return leaves.map((leaf) => {
    const showGroupHeader = !!leaf.group && leaf.groupId !== previousGroupId;
    previousGroupId = leaf.groupId;
    return { ...leaf, showGroupHeader, level: null };
  });
}

/** Die Gruppe, unter der eine Zeile steht: der naechste Modulknoten oberhalb. */
type GroupInfo = Pick<Leaf, 'group' | 'groupId' | 'groupStatus' | 'groupIteration'>;

function collectLeaves(
  byParent: Map<string | null, TestStepResult[]>,
  parentId: string | null,
  prefix: string,
  groupInfo: GroupInfo,
  leaves: Leaf[],
): void {
  const children = byParent.get(parentId) ?? [];
  children.forEach((result, index) => {
    const number = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
    if (result.kind === 'MODULE') {
      collectLeaves(
        byParent,
        result.id,
        number,
        {
          group: result.descriptionSnapshot ?? groupInfo.group,
          groupId: result.id,
          groupStatus: result.status,
          groupIteration: result.iterationIndex,
        },
        leaves,
      );
    } else {
      leaves.push({ result, number, ...groupInfo, indented: number.includes('.') });
      collectLeaves(byParent, result.id, number, groupInfo, leaves);
    }
  });
}
